import React, { useState, useEffect, useRef } from 'react';
import { crmApi } from '../services/crmApi';
import {
  ShieldCheck, Send, CheckCircle2, Clock, AlertCircle, Loader2,
  ExternalLink, Copy, MousePointerClick, Phone, Eye, ChevronDown, ChevronUp,
  PenTool, RefreshCw
} from 'lucide-react';

interface Props {
  applicationId: number;
  applicationEmail?: string;
  applicationPhone?: string;
  firstName?: string;
  lastName?: string;
}

interface AcceptanceLatest {
  id: number;
  status: string;
  accepted_at: string | null;
  accepted_ip: string | null;
  accepted_user_agent: string | null;
  sms_sent_to: string | null;
  acceptance_token: string | null;
  acceptanceMethod: string | null; // 'sms_verification' | 'direct_confirmation' | 'autenti_esignature'
  acceptanceUrl: string | null;
  created_at: string;
  created_by: number;
  signing_method: string | null;
  autenti_process_id: string | null;
  autenti_status: string | null;
  autenti_signing_url: string | null;
  autenti_completed_at: string | null;
}

interface AcceptanceStatus {
  hasAcceptance: boolean;
  latest: AcceptanceLatest | null;
  history: any[];
  followUps: any[];
}

export const ContractAcceptanceSection: React.FC<Props> = ({
  applicationId,
  applicationEmail,
  applicationPhone,
  firstName,
  lastName,
}) => {
  const [status, setStatus] = useState<AcceptanceStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [copied, setCopied] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showContract, setShowContract] = useState(false);
  const [grammar, setGrammar] = useState<Record<string, string> | null>(null);
  const [signingMethod, setSigningMethod] = useState<'direct' | 'sms' | 'autenti'>('direct');
  const [checkingAutenti, setCheckingAutenti] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    loadStatus();
    if (firstName && lastName) loadGrammar();
    return () => { mountedRef.current = false; };
  }, [applicationId]);

  const loadStatus = async () => {
    try {
      setLoading(true);
      const data = await crmApi.getContractAcceptanceStatus(applicationId);
      if (mountedRef.current) setStatus(data);
    } catch {
      if (mountedRef.current) setStatus({ hasAcceptance: false, latest: null, history: [], followUps: [] });
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  };

  const loadGrammar = async () => {
    try {
      const data = await crmApi.getGrammarPreview(firstName!, lastName!, applicationId);
      if (mountedRef.current) setGrammar(data.grammar);
    } catch {}
  };

  const handleCreate = async () => {
    if (!applicationEmail) {
      setError('Brak adresu email wnioskodawcy');
      return;
    }
    try {
      setCreating(true);
      setError('');
      setSuccess('');
      const result = await crmApi.createContractAcceptance(applicationId, signingMethod);
      if (signingMethod === 'autenti') {
        setSuccess(result.autentiProcessId
          ? `Dokument wysłany do podpisu Autenti (${applicationEmail})`
          : `Akceptacja utwórzona, ale wysyłka do Autenti nie powiodła się`);
      } else {
        setSuccess(`Link wysłany na ${applicationEmail}`);
      }
      loadStatus();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Błąd tworzenia akceptacji');
    } finally {
      if (mountedRef.current) setCreating(false);
    }
  };

  const handleCheckAutentiStatus = async () => {
    const token = status?.latest?.acceptance_token;
    if (!token) return;
    try {
      setCheckingAutenti(true);
      const result = await crmApi.checkAutentiStatus(token);
      if (result.status === 'zaakceptowana') {
        setSuccess('Dokument podpisany przez Autenti!');
      }
      loadStatus();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Błąd sprawdźania statusu Autenti');
    } finally {
      if (mountedRef.current) setCheckingAutenti(false);
    }
  };

  const handleCopyLink = async () => {
    const url = status?.latest?.acceptanceUrl;
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  const statusConfig: Record<string, { color: string; bg: string; border: string; icon: React.ReactNode; label: string }> = {
    oczekujaca: {
      color: 'text-yellow-700', bg: 'bg-yellow-50', border: 'border-yellow-200',
      icon: <Clock size={16} className="text-yellow-600" />,
      label: 'Oczekuje na akceptację',
    },
    sms_wyslany: {
      color: 'text-blue-700', bg: 'bg-blue-50', border: 'border-blue-200',
      icon: <Send size={16} className="text-blue-600" />,
      label: 'Kod SMS wysłany',
    },
    zaakceptowana: {
      color: 'text-green-700', bg: 'bg-green-50', border: 'border-green-200',
      icon: <CheckCircle2 size={16} className="text-green-600" />,
      label: 'Zaakceptowano',
    },
    wygasla: {
      color: 'text-gray-700', bg: 'bg-gray-50', border: 'border-gray-200',
      icon: <AlertCircle size={16} className="text-gray-500" />,
      label: 'Wygasło',
    },
  };

  const methodLabel = (method: string | null) => {
    if (method === 'sms_verification') return { icon: <Phone size={12} />, text: 'SMS', color: 'bg-blue-100 text-blue-700' };
    if (method === 'direct_confirmation') return { icon: <MousePointerClick size={12} />, text: 'Bezpośrednio', color: 'bg-green-100 text-green-700' };
    if (method === 'autenti_esignature') return { icon: <PenTool size={12} />, text: 'Autenti', color: 'bg-purple-100 text-purple-700' };
    return null;
  };

  const autentiStatusLabel = (s: string | null) => {
    if (!s) return null;
    const map: Record<string, { label: string; color: string }> = {
      'in_progress': { label: 'Oczekuje na podpis', color: 'text-blue-600' },
      'completed': { label: 'Podpisano', color: 'text-green-600' },
      'rejected': { label: 'Odrzućono', color: 'text-red-600' },
      'expired': { label: 'Wygasło', color: 'text-gray-500' },
      'withdrawn': { label: 'Wycofano', color: 'text-gray-500' },
      'error': { label: 'Błąd wysyłki', color: 'text-red-600' },
      'draft': { label: 'Szkic', color: 'text-yellow-600' },
    };
    return map[s] || { label: s, color: 'text-gray-600' };
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center gap-2 text-gray-500 text-sm">
          <Loader2 className="animate-spin" size={16} />
          Ładowanie statusu akceptacji...
        </div>
      </div>
    );
  }

  const latest = status?.latest;
  const cfg = latest ? statusConfig[latest.status] || statusConfig.oczekujaca : null;
  const method = latest
    ? methodLabel(latest.acceptanceMethod) ||
      (latest.signing_method === 'autenti' ? { icon: <PenTool size={12} />, text: 'Autenti', color: 'bg-purple-100 text-purple-700' } : null)
    : null;

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="font-bold flex items-center text-lg mb-4">
        <ShieldCheck className="mr-2 text-indigo-600" size={22} />
        Akceptacja warunków
      </h3>

      {/* Grammar preview */}
      {grammar && (
        <div className="mb-4 p-3 bg-indigo-50 border border-indigo-100 rounded-lg">
          <p className="text-xs font-semibold text-indigo-700 mb-1">Odmiana polska (podgląd):</p>
          <div className="text-xs text-indigo-900 space-y-0.5">
            <p><span className="text-indigo-500">Zwrot:</span> {grammar.formalAddressFull}</p>
            <p><span className="text-indigo-500">Dopełniacz:</span> {grammar.fullNameGenitive}</p>
            <p><span className="text-indigo-500">Celownik:</span> {grammar.fullNameDative}</p>
            <p><span className="text-indigo-500">Narzędnik:</span> {grammar.fullNameInstrumental}</p>
            <p><span className="text-indigo-500">Wołacz:</span> {grammar.formalAddress === 'Pan' ? 'Panie' : 'Pani'} {grammar.firstNameVocative}</p>
          </div>
        </div>
      )}

      {/* Current status */}
      {latest && cfg && (
        <div className={`border rounded-lg p-3 mb-4 ${cfg.bg} ${cfg.border}`}>
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2">
              {cfg.icon}
              <span className={`font-medium text-sm ${cfg.color}`}>{cfg.label}</span>
            </div>
            {method && (
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${method.color}`}>
                {method.icon} {method.text}
              </span>
            )}
          </div>
          <div className="text-xs text-gray-500 space-y-0.5">
            <p>Utwórzono: {new Date(latest.created_at).toLocaleString('pl-PL')}</p>
            {latest.accepted_at && (
              <p>Zaakceptowano: {new Date(latest.accepted_at).toLocaleString('pl-PL')}</p>
            )}
            {latest.accepted_ip && <p>IP: {latest.accepted_ip}</p>}
            {latest.sms_sent_to && <p>SMS na: ***{latest.sms_sent_to.slice(-3)}</p>}
            {latest.signing_method === 'autenti' && latest.autenti_status && (() => {
              const as = autentiStatusLabel(latest.autenti_status);
              return as ? <p className={as.color}>Autenti: {as.label}</p> : null;
            })()}
            {latest.autenti_completed_at && (
              <p>Podpisano Autenti: {new Date(latest.autenti_completed_at).toLocaleString('pl-PL')}</p>
            )}
          </div>

          {/* Action buttons for active acceptance */}
          {latest.status !== 'wygasla' && (
            <div className="flex gap-2 mt-3 flex-wrap">
              {latest.acceptanceUrl && (
                <>
                  <button
                    onClick={handleCopyLink}
                    className="flex items-center gap-1 px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-xs hover:bg-gray-50 transition-colors"
                  >
                    <Copy size={12} />
                    {copied ? 'Skopiowano!' : 'Kopiuj link'}
                  </button>
                  <button
                    onClick={() => window.open(latest.acceptanceUrl!, '_blank')}
                    className="flex items-center gap-1 px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-xs hover:bg-gray-50 transition-colors"
                  >
                    <ExternalLink size={12} />
                    Otwórz
                  </button>
                </>
              )}
              {latest.signing_method === 'autenti' && latest.autenti_signing_url && latest.status !== 'zaakceptowana' && (
                <button
                  onClick={() => window.open(latest.autenti_signing_url!, '_blank')}
                  className="flex items-center gap-1 px-3 py-1.5 bg-purple-50 border border-purple-200 rounded-lg text-xs text-purple-700 hover:bg-purple-100 transition-colors"
                >
                  <PenTool size={12} />
                  Otwórz podpis Autenti
                </button>
              )}
              {latest.signing_method === 'autenti' && latest.status !== 'zaakceptowana' && (
                <button
                  onClick={handleCheckAutentiStatus}
                  disabled={checkingAutenti}
                  className="flex items-center gap-1 px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-xs hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  <RefreshCw size={12} className={checkingAutenti ? 'animate-spin' : ''} />
                  {checkingAutenti ? 'Sprawdźanie...' : 'Odśwież status'}
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Error / Success messages */}
      {error && (
        <div className="text-sm text-red-600 bg-red-50 p-2 rounded mb-3">{error}</div>
      )}
      {success && (
        <div className="text-sm text-green-600 bg-green-50 p-2 rounded mb-3">{success}</div>
      )}

      {/* Create new - no acceptance or expired */}
      {(!latest || latest.status === 'wygasla') && (
        <div>
          <p className="text-sm text-gray-600 mb-3">
            Wyślij email z linkiem do akceptacji warunków współpracy.
          </p>
          <div className="text-xs text-gray-400 mb-3">
            {applicationEmail ? `Email: ${applicationEmail}` : 'Brak emaila!'}
            {applicationPhone ? ` | Tel: ***${applicationPhone.slice(-3)}` : ' | Brak telefonu!'}
          </div>

          {/* Signing method selector */}
          <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-100">
            <p className="text-xs font-semibold text-gray-600 mb-2">Metoda akceptacji:</p>
            <div className="flex flex-col gap-1.5">
              <label className="flex items-center gap-2 cursor-pointer text-xs">
                <input
                  type="radio"
                  name="signingMethod"
                  value="direct"
                  checked={signingMethod === 'direct'}
                  onChange={() => setSigningMethod('direct')}
                  className="w-3.5 h-3.5 text-indigo-600"
                />
                <MousePointerClick size={13} className="text-green-600" />
                <span>Bezpośrednia (checkbox + IP)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer text-xs">
                <input
                  type="radio"
                  name="signingMethod"
                  value="sms"
                  checked={signingMethod === 'sms'}
                  onChange={() => setSigningMethod('sms')}
                  className="w-3.5 h-3.5 text-indigo-600"
                />
                <Phone size={13} className="text-blue-600" />
                <span>Weryfikacja SMS</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer text-xs">
                <input
                  type="radio"
                  name="signingMethod"
                  value="autenti"
                  checked={signingMethod === 'autenti'}
                  onChange={() => setSigningMethod('autenti')}
                  className="w-3.5 h-3.5 text-indigo-600"
                />
                <PenTool size={13} className="text-purple-600" />
                <span>Podpis elektroniczny Autenti</span>
              </label>
            </div>
          </div>

          <button
            onClick={handleCreate}
            disabled={creating || !applicationEmail}
            className={`w-full text-white py-2.5 rounded-lg font-medium disabled:opacity-50 flex items-center justify-center text-sm transition-all ${
              signingMethod === 'autenti'
                ? 'bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700'
                : 'bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700'
            }`}
          >
            {creating ? (
              <><Loader2 className="animate-spin mr-2" size={16} />Wysyłanie...</>
            ) : signingMethod === 'autenti' ? (
              <><PenTool className="mr-2" size={16} />Wyślij do podpisu Autenti</>
            ) : (
              <><Send className="mr-2" size={16} />Wyślij link akceptacji</>
            )}
          </button>
        </div>
      )}

      {/* Re-send for pending/sms_sent */}
      {latest && (latest.status === 'oczekujaca' || latest.status === 'sms_wyslany') && (
        <div className="text-center">
          <p className="text-sm text-gray-500 mb-2">
            Link został wysłany. Klient nie zaakceptował jeszcze warunków.
          </p>
          <button
            onClick={handleCreate}
            disabled={creating}
            className="text-sm text-indigo-600 hover:underline disabled:opacity-50"
          >
            {creating ? 'Tworzenie nowego linku...' : 'Wyślij ponownie (nowy link)'}
          </button>
        </div>
      )}

      {/* Accepted */}
      {latest && latest.status === 'zaakceptowana' && (
        <div className="text-center text-sm text-green-700 mb-3">
          Klient zaakceptował warunki współpracy.
        </div>
      )}

      {/* History toggle */}
      {status && status.history.length > 1 && (
        <div className="mt-4 border-t pt-3">
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"
          >
            {showHistory ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            Historia ({status.history.length} {status.history.length === 1 ? 'wpis' : status.history.length < 5 ? 'wpisy' : 'wpisów'})
          </button>
          {showHistory && (
            <div className="mt-2 space-y-2">
              {status.history.map((item, idx) => {
                const itemCfg = statusConfig[item.status] || statusConfig.oczekujaca;
                return (
                  <div key={item.id} className={`flex items-center gap-2 text-xs p-2 rounded ${idx === 0 ? 'bg-gray-50' : ''}`}>
                    {itemCfg.icon}
                    <span className={itemCfg.color}>{itemCfg.label}</span>
                    <span className="text-gray-400 ml-auto">
                      {new Date(item.created_at).toLocaleDateString('pl-PL')}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
